import { useEffect } from 'react';

import { Button, Flex, FloraButton, Input } from '@grupoboticario/flora-react';
import { EyeIcon, EyeOffIcon } from '@grupoboticario/flora-react-icons';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { SubmitHandler } from 'react-hook-form';
import { Navigate, useNavigate } from 'react-router-dom';
import slugify from 'slugify';

import { ResponsiveFloraTypography } from 'components/ResponsiveFloraTypography';
import { portugueseCadastralChangeName } from 'helpers';
import { useLogin, useTracking } from 'hooks';
import { useLoginForm } from 'hooks/useLoginForm';
import { ResetPassword } from 'pages/Profile/components';
import { newRelicMonitoringService } from 'services';
import { ACCESS_TOKEN_KEY_CADASTRAL_CHANGE } from 'services/auth';
import { getRequestId } from 'services/create-request-id';
import { getRecaptchaToken } from 'services/recaptcha';
import { useGlobalContext, useRegistrationControllerContext } from 'store';

import { MessageFormError } from '../components';
import Styles from '../Drawers.styles';

import type { AnalyticsType } from 'domains/analytics.d';
import type { AuthCredentials } from 'domains/auth.d';

type PasswordValidateProps = {
  type: string;
  nextStep: string;
};

export const PasswordValidate = ({ type, nextStep }: PasswordValidateProps) => {
  const { user } = useGlobalContext();
  const { steps, handleValidate } = useRegistrationControllerContext();
  const {
    canSubmit,
    handleTracking,
    handleSubmit,
    rules,
    passwordType,
    showPassword,
    register,
    isInvalidForm,
    toggleInvalidForm,
  } = useLoginForm();

  const { mutate: login, isLoading, isSuccess, isError, error } = useLogin();
  const navigate = useNavigate();
  const { sendDataToGTM } = useTracking();

  const onSubmit: SubmitHandler<AuthCredentials> = async (inputData) => {
    const recaptchaToken = await getRecaptchaToken(user.resellerId, 'login');
    Cookies.set('X-Recaptcha-Token', typeof recaptchaToken === 'string' ? recaptchaToken : '');

    login({ username: user.resellerId, password: inputData.password });
  };

  const handleAnalytics = (status: 'erro' | 'sucesso', err?: AxiosError) => {
    const analyticsData: AnalyticsType = {
      event: 'analytics-event',
      event_parameters: {
        event_name: `callback_senha_${portugueseCadastralChangeName[type]}_${status}`,
        ...(status === 'erro' && {
          cd_error_message: slugify(err?.message ?? '', {
            lower: true,
            replacement: '-',
          }),
        }),
      },
    };

    newRelicMonitoringService.addPageAction('callback_cadastral_change', {
      event_name: `auth_${status}`,
      page_title: `alteração cadastral - ${portugueseCadastralChangeName[type]}`,
      change_type: type,
      error_status: err?.status,
      error_message: err?.message,
      username: user.resellerId,
      session_id: getRequestId() || '',
    });

    sendDataToGTM(analyticsData);
  };

  useEffect(() => {
    if (isSuccess) {
      handleValidate();
      navigate(nextStep, { replace: true });
      handleAnalytics('sucesso');
    }

    if (isError) {
      toggleInvalidForm(true);
      handleAnalytics('erro', error as AxiosError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isSuccess, isError]);

  const handleTrackingInfo = (interaction: string) => {
    sendDataToGTM({
      event: 'analytics-event',
      event_parameters: {
        event_name: `interaction_alterar_${portugueseCadastralChangeName[type]}`,
        cd_interaction_detail: interaction,
      },
    });
  };

  const cancelFlow = () => {
    Cookies.remove(ACCESS_TOKEN_KEY_CADASTRAL_CHANGE);
    navigate('/perfil', { replace: true });

    handleTrackingInfo('click:cancelar');
  };

  if (!steps.start) {
    return <Navigate to='/perfil' replace />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Styles.DrawerSection align='center' direction='column' gap='$8'>
        <Styles.FullFlex direction='column' gap='$6'>
          <ResponsiveFloraTypography
            fontSize='bodySmallShort'
            fontWeight='bold'
            color='$backgroundAltSecondary'
          >
            Por segurança, insira a sua senha de acesso ao portal.
          </ResponsiveFloraTypography>
          <Flex direction='column'>
            <Input
              autoComplete='none'
              id='password'
              labelText='Senha'
              aria-label='senha'
              required
              data-title='input:inserir-senha'
              data-eventname={`interaction_alterar_${portugueseCadastralChangeName[type]}`}
              invalidText=''
              invalid={isInvalidForm}
              disabled={isLoading}
              rightIcon={
                <Button
                  as='span'
                  variant='unstyled'
                  data-eventname={`interaction_alterar_${portugueseCadastralChangeName[type]}`}
                  data-title='click:mostrar-senha'
                  onClick={showPassword}
                  aria-label={
                    passwordType
                      ? 'ativar visualização de caracteres'
                      : 'desativar visualização de caracteres'
                  }
                >
                  {passwordType === 'password' ? <EyeIcon /> : <EyeOffIcon />}
                </Button>
              }
              type={passwordType === 'password' ? 'password' : 'text'}
              {...register('password', rules.password)}
              onBlur={handleTracking}
            />
            {isError && <MessageFormError message={error?.message} />}

            <ResetPassword
              label='Esqueci minha senha'
              location={`perfil/editar/${portugueseCadastralChangeName[type]}/modal-esqueci-minha-senha`}
              eventName={`interaction_alterar_${portugueseCadastralChangeName[type]}`}
              pageView='view-modal-esqueci-minha-senha'
            />
          </Flex>
        </Styles.FullFlex>

        <Styles.FullFlex direction='column' gap='$2'>
          <FloraButton
            type='submit'
            isFull
            hierarchy='primary'
            disabled={!canSubmit || isLoading}
            isLoading={isLoading}
          >
            <ResponsiveFloraTypography fontSize='bodyLargeShort'>
              Continuar
            </ResponsiveFloraTypography>
          </FloraButton>
          <FloraButton
            autotagging-flora='on'
            data-flora='button'
            data-flora-text='cancelar'
            hierarchy='secondary'
            isFull
            onClick={cancelFlow}
            disabled={isLoading}
          >
            <ResponsiveFloraTypography fontSize='bodyLargeShort'>
              Cancelar
            </ResponsiveFloraTypography>
          </FloraButton>
        </Styles.FullFlex>
      </Styles.DrawerSection>
    </form>
  );
};
export default PasswordValidate;
